import { Suspense, lazy, useEffect, useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@components/ui';
import Accordion from '@components/ui/Accordion/Accordion';
import { DraggableTabs } from '@components/shared/DraggableTabs/DraggableTabs';
import ProfileCard from '@features/tenant-profile/ProfileCard';
import type { TabItem } from '@components/shared/DraggableTabs/types';
import { getFormTemplateById } from '@/services/formTemplateService';
import { useCountries } from '@/hooks/useCountries';
import { useTranslation } from '@/hooks/useTranslation';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import EditIcon from '@assets/icons/EditIcon';
import styles from './EntityTemplateView.module.scss';

const FormPreview = lazy(() => import('@features/tenant-templates/FormPreview'));

interface EntityTemplateViewProps {
  templateId: string;
}

function EntityTemplateView({ templateId }: EntityTemplateViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setOverride = useBreadcrumbOverride((s) => s.setOverride);

  const { data: template, isLoading } = useQuery({
    queryKey: ['formTemplate', templateId],
    queryFn: () => getFormTemplateById(templateId),
  });

  // Dynamic breadcrumb: replace static "View Template" with actual template name
  useEffect(() => {
    if (template?.name) {
      setOverride(`/w/entity-templates/view/${templateId}`, template.name);
      return () => setOverride(`/w/entity-templates/view/${templateId}`, null);
    }
  }, [template?.name, templateId, setOverride]);

  const { countries } = useCountries();

  const countryMap = useMemo(
    () => new Map(countries.map((c) => [c.id, c.name])),
    [countries],
  );

  const [activeTabId, setActiveTabId] = useState('');

  // Set initial active tab when data loads
  const tabs = template?.tabs ?? [];
  if (tabs.length > 0 && !activeTabId) {
    const firstTab = tabs[0];
    if (firstTab) {
      setActiveTabId(`tab-${0}`);
    }
  }

  const tabItems: TabItem[] = useMemo(
    () =>
      tabs.map((tab, i) => ({
        id: `tab-${i}`,
        label: tab.name || `Tab ${i + 1}`,
      })),
    [tabs],
  );

  const activeTabIndex = tabItems.findIndex((t) => t.id === activeTabId);
  const activeTab = activeTabIndex >= 0 ? tabs[activeTabIndex] : undefined;

  const handleTabClick = useCallback((tab: TabItem) => {
    setActiveTabId(tab.id);
  }, []);

  const handleEdit = useCallback(() => {
    void navigate({ to: '/w/entity-templates/add', search: { id: templateId } });
  }, [navigate, templateId]);

  const profileFields = useMemo(() => {
    if (!template) return [];
    const countryName = template.countryId ? countryMap.get(template.countryId) ?? '-' : '-';
    return [
      { label: t('K_LABEL_COUNTRY', 'Country'), value: countryName },
      { label: t('K_LABEL_TEMPLATE_DESCRIPTION', 'Description'), value: template.description || '-' },
      { label: t('K_COLUMN_VERSION', 'Version'), value: String(template.version) },
      {
        label: t('K_COLUMN_CREATED', 'Created'),
        value: new Date(template.createdAt).toLocaleDateString(),
      },
      {
        label: t('K_COLUMN_UPDATED', 'Updated'),
        value: new Date(template.updatedAt).toLocaleDateString(),
      },
    ];
  }, [template, countryMap, t]);

  if (isLoading) {
    return <ProfileCard name="" fields={[]} loading />;
  }

  if (!template) {
    return (
      <div className={styles.errorState}>
        <p>{t('K_ERROR_TEMPLATE_NOT_FOUND', 'Template not found')}</p>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <ProfileCard
        name={template.name || t('K_LABEL_ENTITY_TEMPLATE', 'Entity Template')}
        fields={profileFields}
        avatarColor="blue"
        action={
          <Button variant="outline" size="sm" leftIcon={<EditIcon />} onClick={handleEdit}>
            {t('K_BUTTON_EDIT_TEMPLATE', 'Edit Template')}
          </Button>
        }
      />

      {tabs.length > 0 ? (
        <>
          <DraggableTabs
            tabs={tabItems}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            onReorder={() => {}}
            draggable={false}
          />

          <div className={styles.tabContent}>
            {activeTab ? (
              <div className={styles.tabInfo}>
                <div className={styles.tabMeta}>
                  <div className={styles.tabMetaField}>
                    <span className={styles.tabMetaLabel}>
                      {t('K_LABEL_TAB_NAME', 'Tab Name')}
                    </span>
                    <span className={styles.tabMetaValue}>{activeTab.name || '-'}</span>
                  </div>
                  <div className={styles.tabMetaField}>
                    <span className={styles.tabMetaLabel}>
                      {t('K_LABEL_TAB_DESCRIPTION', 'Description')}
                    </span>
                    <span className={styles.tabMetaValue}>{activeTab.description || '-'}</span>
                  </div>
                </div>

                <div className={styles.formList}>
                  {activeTab.forms.map((form, i) => (
                    <Accordion
                      key={form.formConfigId}
                      title={form.formName || `Form ${i + 1}`}
                      variant="filled"
                      whiteBody
                    >
                      <div className={styles.formSettings}>
                        <div className={styles.settingItem}>
                          <span className={styles.settingLabel}>
                            {t('K_LABEL_MANDATORY_FORM_ENTITY', 'Mandatory form while creating entity')}
                          </span>
                          <span className={styles.settingValue}>
                            {form.attributes.isMandatory
                              ? t('K_LABEL_YES', 'Yes')
                              : t('K_LABEL_NO', 'No')}
                          </span>
                        </div>
                      </div>
                      <div className={styles.previewSection}>
                        <h4 className={styles.previewHeader}>
                          {t('K_LABEL_FORM_PREVIEW', 'Form Preview')}
                        </h4>
                        <Suspense fallback={<div>{t('K_LOADING', 'Loading...')}</div>}>
                          <FormPreview formConfigId={form.formConfigId} />
                        </Suspense>
                      </div>
                    </Accordion>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default EntityTemplateView;
